import { isEqual } from 'lodash';

import { Template } from './template.model';
import { StyleStateEnum } from '../../PageBuilder/enums/style-state.enum';

// F.E accidentally stores these fields in model so make them const outside of the model
const excludeFromEquals = [
    'topMentionsMetadata',
    'topCuratedMentions',
    'outletPhotoMap',
    'socialTopicIds',
    'dateRange',
    'labelDisplayOptions',
    'sentiments',
    'dateRangeLabelText',
    'currency'
];

const includedProperties = [
    'styleState',
    'displayStyleState',
    'template',
    'defaultReference',
    'includedProperties'
];

export class GenericStyleModel {
    styleState?: number | string;
    displayStyleState?: number | string;
    template?: Template;
    defaultReference?: string;
    [key: string]: any;

    constructor(apiModel?: any) {
        if (apiModel) {
            this.setFromApiModel(apiModel);
        }
    }

    /**
     * Returns a clone of this style model
     */
    clone(base?: GenericStyleModel): GenericStyleModel {
        return new GenericStyleModel(this.toBlockStyle());
    };

    equals(other: GenericStyleModel, compareTemplate: boolean): boolean {
        if (this.styleState !== other.styleState) {
            return false;
        }

        if (this.displayStyleState !== other.displayStyleState) {
            return false;
        }

        if (
            compareTemplate &&
            this.template &&
            !this.template.equals(other.template)
        ) {
            return false;
        }

        for (const property in this) {
            if (!this.hasOwnProperty(property) || includedProperties.includes(property) || excludeFromEquals.includes(property)) {
                continue;
            }

            // weird scenario where doing a match on the stringify fails even though the objects are the same, because when we go to stringify, the order of the
            // variables are not the same, and when you compare the string, they are obviously different...
            // then we got into a bug where you cannot parse undefined objects, so if the values are undefined, check on null instead
            const truthinessdiffers = !!this[property] !== !!other[property];
            const px = JSON.parse(JSON.stringify(this[property], (k, v) => !!v ? v : null));
            const py = JSON.parse(JSON.stringify(other[property], (k, v) => !!v ? v : null));
            const notDeepEqual = !isEqual(px, py);
            if (truthinessdiffers || notDeepEqual) {
                return false;
            }
        }
        return true;
    };

    /**
     * Extract the fields that get saved to saved style
     */
    toApi(): any {
        const apiModel: any = {};

        if (this.defaultReference) {
            apiModel.defaultReference = this.defaultReference;
        }
        for (const property in this) {
            if (!this.hasOwnProperty(property) || includedProperties.includes(property)) {
                continue;
            }

            apiModel[property] = this[property];
        }

        return apiModel;
    };

    /**
     * Extract the fields that get saved to a block
     */
    toBlockStyle(): any {
        const blockStyle = this.toApi();
        blockStyle.styleState = this.styleState;
        blockStyle.displayStyleState = this.displayStyleState;

        return blockStyle;
    };

    setFromApiModel(apiModel: any): void {
        if (typeof apiModel === 'string') {
            apiModel = JSON.parse(apiModel);
        }
        if (apiModel?.hasOwnProperty('styleState')) {

            this.styleState = apiModel.styleState;
            if (this.styleState === StyleStateEnum.CUSTOM) {
                this.displayStyleState = StyleStateEnum.CUSTOM;
            }
        }
        if (apiModel?.hasOwnProperty('displayStyleState')) {
            this.displayStyleState = apiModel.displayStyleState;
        } else if (this.displayStyleState === undefined) {
            this.displayStyleState = this.styleState;
        }
        if (apiModel?.hasOwnProperty('defaultReference')) {
            this.defaultReference = apiModel.defaultReference;
        }
        if (apiModel?.hasOwnProperty('template')) {
            this.template = apiModel.template;
        }

        for (const apiProperty in apiModel) {
            if (!apiModel.hasOwnProperty(apiProperty) || includedProperties.includes(apiProperty)) {
                continue;
            }

            this[apiProperty] = apiModel[apiProperty];
        }
    }

    public isSavedStyle(): boolean {
        return !!Number(this.styleState);
    }

    public getDefaultReference(): string {
        if (this.isSavedStyle() || this.styleState === StyleStateEnum.CUSTOM) {
            return this.defaultReference;
        } else {
            return this.styleState as string;
        }
    }
}
